import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { PaginationComponent } from '../../pagination/pagination.component';
import { GetUser } from 'src/app/models/security/get-user';
import { GetUserFilter } from 'src/app/models/security/get-user-filter';
import { Paged } from 'src/app/models/shared/paged';
import { AlertService } from 'src/app/services/alert.service';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { UserService } from 'src/app/services/user.service';
import { UsersModalComponent } from './users-modal/users-modal.component';
import { FormErrorsComponent } from 'src/app/components/form-errors/form-errors.component';

@Component({
  selector: 'app-users-admin',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule, PaginationComponent, MatDialogModule],
  templateUrl: './users-admin.component.html',
  styles: ``
})
export class UsersAdminComponent implements OnInit {
  // Filtros y estado
  showFilters: boolean = false;
  isLoading: boolean = false;
  filter: GetUserFilter = {
    page: 1,
    pageSize: 10,
    filterKey: null,
    filterValue: null
  };
  
  // Datos
  users: GetUser[] = [];
  totalItems: number = 0;
  
  constructor(
    private userService: UserService,
    private fileStorage: FileStorageService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {}
  
  ngOnInit(): void {
    this.loadUsers();
  }
  
  showFilter(): void {
    this.showFilters = !this.showFilters;
  }
  
  loadUsers(): void {
    this.isLoading = true;
    this.userService.getUsers(this.filter).subscribe({
      next: (response: Paged<GetUser>) => {
        this.users = response.items;
        this.totalItems = response.totalItems;
        this.isLoading = false;
      },
      error: (error) => {
        this.alertService.showErrorAlert('Error', 'No se pudieron cargar los usuarios');
        console.error('Error loading users:', error);
        this.isLoading = false;
      }
    });
  }
  
  onSearch(): void {
    this.filter.page = 1; // Reiniciar a la primera página al buscar
    this.loadUsers();
  }
  
  onPageChange(page: number): void {
    this.filter.page = page;
    this.loadUsers();
  }
  
  onPageSizeChange(pageSize: number): void {
    this.filter.pageSize = pageSize;
    this.filter.page = 1; // Reiniciar a la primera página al cambiar el tamaño
    this.loadUsers();
  }
  
  blockUser(userId: number): void {
    this.alertService.showConfirmDialog(
      '¿Bloquear usuario?', 
      '¿Está seguro que desea bloquear este usuario? El usuario no podrá acceder al sistema mientras esté bloqueado.',
      'Sí, bloquear',
      'Cancelar'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.userService.block(userId).subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Usuario bloqueado', 'El usuario ha sido bloqueado exitosamente');
            this.loadUsers(); // Recargar la lista
          },
          error: (error) => {
            this.alertService.showErrorAlert('Error', 'No se pudo bloquear el usuario');
            console.error('Error blocking user:', error);
          }
        });
      }
    });
  }
  
  unblockUser(userId: number): void {
    this.alertService.showConfirmDialog(
      '¿Desbloquear usuario?', 
      '¿Está seguro que desea desbloquear este usuario? El usuario podrá acceder nuevamente al sistema.',
      'Sí, desbloquear',
      'Cancelar'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.userService.unblock(userId).subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Usuario desbloqueado', 'El usuario ha sido desbloqueado exitosamente');
            this.loadUsers(); // Recargar la lista
          },
          error: (error) => {
            this.alertService.showErrorAlert('Error', 'No se pudo desbloquear el usuario');
            console.error('Error unblocking user:', error);
          }
        });
      }
    });
  }

  openAddUserModal(): void {
    const dialogRef = this.dialog.open(UsersModalComponent, {
      width: '80%',
      maxWidth: '98%',
      disableClose: true,
      data: {
        mode: 'add'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadUsers(); // Recargar la lista si se creó un usuario
      }
    });
  }

  openEditUserModal(user: GetUser): void {
    const dialogRef = this.dialog.open(UsersModalComponent, {
      width: '80%',
      maxWidth: '98%',
      disableClose: true,
      data: {
        title: 'Editar usuario',
        mode: 'edit',
        item: user
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadUsers(); // Recargar la lista si se editó el usuario
      }
    });
  }

  openViewUserModal(user: GetUser): void {
    this.dialog.open(UsersModalComponent, {
      width: '80%',
      maxWidth: '98%',
      data: {
        title: 'Detalles del usuario',
        mode: 'view',
        item: user,
        view: true
      }
    });
  }

  resetPassword(userId: number): void {
    this.alertService.showConfirmDialog(
      'Resetear contraseña',
      '¿Está seguro que desea resetear la contraseña de este usuario? Se enviará un correo con instrucciones para crear una nueva contraseña.',
      'Sí, resetear',
      'Cancelar'
    ).subscribe(confirmed => {
      if (confirmed) {
        // Aquí iría la llamada al servicio para resetear la contraseña
        // Como no tenemos un método específico, mostramos un mensaje de éxito simulado
        this.alertService.showSuccessAlert('Contraseña reseteada', 'Se ha enviado un correo con instrucciones para crear una nueva contraseña');
      }
    });
  }
}
